import { ContentType, } from '@readsavor/types';
import { pdfUploadService } from './PdfUploadService.js';
// A new, more robust line-breaking logic.
// Two words are considered on the same line if the vertical distance between their centers
// is less than 70% of the height of the first word. This handles variations in font size
// and baseline alignment gracefully.
const LINE_TOLERANCE_RATIO = 0.7;
/**
 * NEW: Standalone function to transform the raw API response into our internal format.
 * This is now the single source of truth for this transformation.
 * @param apiResponse The raw JSON response from the PDF processing microservice.
 * @param fileName The original name of the PDF file.
 * @returns A ParsedContent object ready for the application.
 */
export function transformPdfApiResponse(apiResponse, fileName) {
    const finalParagraphs = [];
    const pageInfos = [];
    let globalParagraphIndex = 0;
    for (const page of apiResponse.pages) {
        pageInfos.push({
            pageIndex: page.page_number - 1,
            width: page.width,
            height: page.height,
        });
        if (page.words.length === 0)
            continue;
        // Start the first line/paragraph
        let currentLineAsParagraph = {
            paragraphId: `p-${globalParagraphIndex}`,
            index: globalParagraphIndex++,
            pageNumber: page.page_number,
            words: [page.words[0]], // Start with the first word
        };
        for (let i = 1; i < page.words.length; i++) {
            const prevWord = page.words[i - 1];
            const currentWord = page.words[i];
            const prevWordHeight = prevWord.rect.bottom - prevWord.rect.top;
            const prevWordCenterY = prevWord.rect.top + prevWordHeight / 2;
            const currentWordCenterY = currentWord.rect.top + (currentWord.rect.bottom - currentWord.rect.top) / 2;
            // Check if the vertical distance between word centers is within tolerance
            if (Math.abs(currentWordCenterY - prevWordCenterY) < prevWordHeight * LINE_TOLERANCE_RATIO) {
                // They are on the same line. Add the current word to the current paragraph.
                currentLineAsParagraph.words.push(currentWord);
            }
            else {
                // Line break detected.
                // 1. Push the completed paragraph (the previous line).
                finalParagraphs.push(currentLineAsParagraph);
                // 2. Start a new paragraph with the current word.
                currentLineAsParagraph = {
                    paragraphId: `p-${globalParagraphIndex}`,
                    index: globalParagraphIndex++,
                    pageNumber: page.page_number,
                    words: [currentWord],
                };
            }
        }
        // Don't forget to push the last paragraph after the loop finishes.
        if (currentLineAsParagraph.words.length > 0) {
            finalParagraphs.push(currentLineAsParagraph);
        }
    }
    return {
        type: ContentType.PDF,
        content: fileName,
        metadata: {
            pdfParagraphs: finalParagraphs,
            totalPages: apiResponse.pages.length,
            pageInfos: pageInfos,
        },
    };
}
/**
 * PDF内容处理器
 * 实现了 ContentProcessor 接口，专门用于处理 PDF 文件。
 * 新的实现将调用后端API，该API会安全地与PDF处理微服务通信。
 */
export class PdfContentProcessor {
    /**
     * 主入口函数：接收 PDF 文件，调用后端API，并返回处理后的结构化数据。
     * @param content - 必须是一个 File 对象。
     */
    async parseContent(content) {
        if (!(content instanceof File)) {
            throw new Error('PdfContentProcessor.parseContent: content must be a File object.');
        }
        const file = content;
        // 1. 调用新的、封装好的 PdfUploadService。
        //    所有复杂的多步操作（授权、上传、轮询）都已在该服务中封装。
        const resultData = await pdfUploadService.process(file);
        // 2. 使用现有的转换函数来格式化 API 响应。
        return transformPdfApiResponse(resultData, file.name);
    }
    /**
     * 为了满足ContentProcessor接口而存在的方法。
     * 对于PDF处理，段落划分在parseContent的复杂流程中完成，此方法不应被直接调用。
     * @param _content - 无需使用
     * @returns 空数组
     */
    splitIntoParagraphs(_content) {
        console.warn('PdfContentProcessor.splitIntoParagraphs should not be called directly. Paragraphs are constructed within parseContent.');
        return [];
    }
}
//# sourceMappingURL=PdfContentProcessor.js.map